import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Trash2, Landmark } from 'lucide-react';
import api from '../lib/axios';
import { euro, dateFmt } from '../lib/format';

function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function Cash() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tab, setTab] = useState<'balance' | 'deposits'>('balance');
  const [showForm, setShowForm] = useState(false);
  const [amount, setAmount] = useState('');
  const [depositDate, setDepositDate] = useState(today());
  const [bankAccount, setBankAccount] = useState('');
  const [error, setError] = useState<string | null>(null);

  const treasuryAccounts = useQuery<any[]>({
    queryKey: ['treasury-accounts'],
    queryFn: () => api.get('/treasury-accounts').then((r) => r.data),
  });

  const bankAccounts = useMemo(
    () => (treasuryAccounts.data ?? []).filter((a) => a.kind === 'BANK'),
    [treasuryAccounts.data],
  );

  const defaultBankNumber = useMemo(() => {
    if (bankAccounts.length === 0) return '';
    return (bankAccounts.find((a) => a.defaultAccount) ?? bankAccounts[0]).accountNumber as string;
  }, [bankAccounts]);

  useEffect(() => {
    if (!bankAccount && defaultBankNumber) setBankAccount(defaultBankNumber);
  }, [bankAccount, defaultBankNumber]);

  const balance = useQuery({
    queryKey: ['cash-balance'],
    queryFn: () => api.get('/cash/balance').then((r) => r.data),
  });

  const deposits = useQuery({
    queryKey: ['cash-deposits'],
    queryFn: () => api.get('/cash/deposits').then((r) => r.data),
  });

  const statusLabel = (status: string) =>
    status === 'DEPOSITED' ? t('cash.statusDeposited') : t('cash.statusInRegister');

  const deposit = useMutation({
    mutationFn: () =>
      api
        .post('/cash/deposit', {
          amount: Number(amount),
          depositDate,
          bankAccount,
        })
        .then((r) => r.data),
    onSuccess: () => {
      setShowForm(false);
      setAmount('');
      setDepositDate(today());
      setBankAccount(defaultBankNumber);
      setError(null);
      qc.invalidateQueries({ queryKey: ['cash-deposits'] });
      qc.invalidateQueries({ queryKey: ['cash-balance'] });
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? t('cash.depositError')),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/cash/deposits/${id}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cash-deposits'] });
      qc.invalidateQueries({ queryKey: ['cash-balance'] });
    },
    onError: (e: any) => window.alert(e?.response?.data?.message ?? t('cash.deleteError')),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!(Number(amount) > 0)) {
      setError(t('cash.depositAmountInvalid'));
      return;
    }
    deposit.mutate();
  };

  return (
    <div>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="eyebrow">{t('nav.cash')}</p>
          <h1 className="page-title mt-1">{t('cash.title')}</h1>
        </div>
        <button type="button" onClick={() => setShowForm(true)} className="btn btn-primary gap-2">
          <Landmark size={16} />
          {t('cash.depositToBank')}
        </button>
      </header>

      <div className="mt-6 flex gap-1 border-b border-line">
        <button
          type="button"
          onClick={() => setTab('balance')}
          className={`px-4 py-2 text-sm font-medium ${
            tab === 'balance' ? 'border-b-2 border-brand text-ink' : 'text-ink-muted'
          }`}
        >
          {t('cash.tabBalance')}
        </button>
        <button
          type="button"
          onClick={() => setTab('deposits')}
          className={`px-4 py-2 text-sm font-medium ${
            tab === 'deposits' ? 'border-b-2 border-brand text-ink' : 'text-ink-muted'
          }`}
        >
          {t('cash.tabDeposits')}
        </button>
      </div>

      {showForm && (
        <div className="card mt-6 p-6">
          <h2 className="font-display text-lg font-semibold">{t('cash.depositToBank')}</h2>
          <p className="mt-1 text-sm text-ink-muted">{t('cash.depositHint')}</p>
          <form onSubmit={submit} className="mt-4 grid gap-4 sm:grid-cols-3">
            <label className="block">
              <span className="mb-1 block text-sm text-ink-soft">{t('cash.depositAmount')}</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="input w-full tnum"
                autoFocus
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm text-ink-soft">{t('checks.depositDate')}</span>
              <input
                type="date"
                value={depositDate}
                onChange={(e) => setDepositDate(e.target.value)}
                className="input w-full"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm text-ink-soft">{t('checks.bankAccount')}</span>
              <select
                value={bankAccount}
                onChange={(e) => setBankAccount(e.target.value)}
                className="select w-full"
              >
                {bankAccounts.length === 0 && <option value="">{t('common.none')}</option>}
                {bankAccounts.map((a: any) => (
                  <option key={a.id} value={a.accountNumber}>
                    {a.name} · {a.accountNumber}
                  </option>
                ))}
              </select>
            </label>
            {error && <p className="text-sm text-expense sm:col-span-3">{error}</p>}
            <div className="flex gap-2 sm:col-span-3">
              <button type="submit" disabled={deposit.isPending} className="btn btn-primary">
                {t('common.save')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setError(null);
                }}
                className="btn btn-ghost"
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="mt-6">
        {tab === 'balance' && (
          <div className="card flex flex-col justify-between p-6 lg:max-w-sm">
            <p className="eyebrow">{t('cash.balance')}</p>
            <div className="mt-4 font-display text-4xl font-semibold tracking-tight tnum">
              {euro(balance.data?.balance)}
            </div>
          </div>
        )}

        {tab === 'deposits' && (
          <div className="card p-0">
            <h2 className="px-6 py-4 font-display text-lg font-semibold">{t('cash.deposits')}</h2>
            <div className="px-3 pb-3 overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('field.date')}</th>
                    <th>{t('field.number')}</th>
                    <th className="num">{t('field.amount')}</th>
                    <th className="text-right">{t('field.status')}</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {(deposits.data ?? []).map((d: any) => (
                    <tr key={d.id}>
                      <td data-label={t('field.date')} className="whitespace-nowrap text-ink-soft">{dateFmt(d.depositDate)}</td>
                      <td data-label={t('field.number')} className="font-medium">{d.number}</td>
                      <td data-label={t('field.amount')} className="num">{euro(d.totalAmount)}</td>
                      <td data-label={t('field.status')} className="text-right text-ink-soft">{statusLabel(d.status)}</td>
                      <td className="cell-actions text-right">
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(t('cash.confirmDelete'))) remove.mutate(d.id);
                          }}
                          title={t('common.delete')}
                          className="btn btn-ghost !px-2 !py-1 text-expense"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {(deposits.data ?? []).length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-ink-muted">
                        {t('cash.empty')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
